export const blogPosts = [
  {
    slug: 'ai-regulation-self-hosting-private-ai',
    title: 'AI regulation and self-hosting: why private deployment is an advantage, not a loophole',
    category: 'Regulatory',
    breadcrumbLabel: 'Regulatory',
    publishedAt: '2026-09-18',
    displayDate: 'September 18, 2026',
    cardFooterLabel: 'Read the analysis',
    excerpt:
      'AI rules are fragmented, extraterritorial and risk-based - and none of them exempt you because your models run on your own hardware. Self-hosting is not a compliance loophole, but it is what makes residency, provenance, auditability and continuity achievable instead of aspirational.',
    seoDescription:
      'The challenges of proposed AI regulation - fragmentation, extraterritoriality, role assignment, risk classification, data provenance and transparency - and how self-hosting and privately operating AI models changes what you can control and prove.',
    blocks: [
      {
        type: 'lead',
        text: 'Every conversation about AI regulation starts with the same wrong question: which law applies? The honest answer is that no single law does. What you are navigating is a fragmented, extraterritorial, risk-based patchwork that overlaps privacy, security and export-control rules - and none of it exempts you because your models run on your own hardware. Self-hosting is not a compliance loophole. It is, however, the thing that makes residency, provenance, auditability and continuity achievable instead of aspirational.',
      },
      {
        type: 'heading',
        text: 'The challenge is fragmentation, not one law',
      },
      {
        type: 'paragraph',
        text: 'There is no single global regime. The EU AI Act takes a risk-based approach and applies extraterritorially, much like GDPR: if your AI systems serve EU users, or their output is used in the EU, you are in scope regardless of where your infrastructure sits. The United States has no unified federal AI statute, but a growing patchwork of state laws and sector regulators - financial services, healthcare, employment - each with their own expectations. China layers algorithm filing, security assessments and content rules on top. The UK remains sector-led, with no single act. A system that satisfies one jurisdiction can violate another, and no single configuration satisfies all of them at once.',
      },
      {
        type: 'paragraph',
        text: 'The timelines move, too. The EU\'s Digital Omnibus package deferred the most demanding high-risk obligations - Annex III systems such as recruitment, credit scoring and law enforcement AI - to December 2027, while GPAI penalty powers and Article 50 transparency duties (chatbot disclosure, synthetic-content marking, deepfake labelling) activate first. The deferral buys preparation time; it does not remove the obligation.',
      },
      {
        type: 'heading',
        text: 'Where the compliance work actually lands',
      },
      {
        type: 'paragraph',
        text: 'Strip away the jurisdictional noise and the same handful of hard problems show up everywhere.',
      },
      {
        type: 'highlight',
        title: 'Four obligations that trip teams up',
        items: [
          'Role assignment - providers, deployers and users carry different duties. Self-host and fine-tune an open-weight model and you may take on provider-level obligations you would otherwise share with a vendor.',
          'Risk classification - "high-risk", "general-purpose" and "systemic risk" are defined by use case, not model. Recruitment, credit, benefits, education and critical infrastructure attract the heaviest duties, and the classification is often the hardest legal question.',
          'Data and provenance - training-data rights, copyright, consent, localization, and records of what data was used where. This is where privacy and AI regimes overlap.',
          'Transparency and oversight - documentation, model cards, logging, disclosure that a user is interacting with AI, synthetic-content marking, and demonstrable human oversight.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Add security and incident-reporting duties, and the pattern is clear: most of the work is evidencing what happened, on what data, under whose authority - not choosing a model.',
      },
      {
        type: 'heading',
        text: 'What self-hosting changes',
      },
      {
        type: 'paragraph',
        text: 'Private operation does not exempt you, but it moves several of these obligations from "ask your vendor" to "configure your platform".',
      },
      {
        type: 'highlight',
        title: 'What private deployment materially helps with',
        items: [
          'Residency and sovereignty - prompts, embeddings, RAG corpora and logs stay in your jurisdiction or air-gapped boundary, without depending on a vendor\'s region map.',
          'Evidence and auditability - because you own the gateway and its logs, you can produce the "who used which model, on what data, with what output" trail regulators ask for.',
          'Provenance and reproducibility - you know and pin the exact weights and version. Silent provider updates cannot invalidate the system you assessed and documented.',
          'Policy as infrastructure - PII redaction, classification-based routing, secrets detection and guardrails enforced once at the gateway rather than per application.',
          'Continuity - access cannot be revoked by a provider\'s policy decision or by geopolitics. The Anthropic Fable 5 suspension, where a government directive cascaded into a global switch-off, is the clearest recent example of why that matters.',
          'Fewer sub-processors - a shorter data-processing chain, clearer accountability, and simpler data-subject access and deletion.',
        ],
      },
      {
        type: 'heading',
        text: 'What self-hosting does not do',
      },
      {
        type: 'paragraph',
        text: 'It is a means, not a certificate. A high-risk system is still high-risk inside your own datacenter, and compliance follows the data, the use case and your role - not the location of the GPUs.',
      },
      {
        type: 'highlight',
        title: 'The traps to avoid',
        items: [
          'Self-hosting does not remove obligations; it shifts the governance work onto you. Risk management, documentation, evaluations, incident response and retention are yours to build.',
          'Open-weight does not mean low-risk. Un-tuned weights may lack safety alignment, and export-control and provenance rules still follow the weights.',
          'Fine-tuning can promote you from deployer to provider, triggering transparency and copyright duties you did not have before.',
          'Security is entirely yours. The blast radius of a private deployment is whatever your boundary, identity and patching allow.',
        ],
      },
      {
        type: 'heading',
        text: 'Mapping obligations to a private AI stack',
      },
      {
        type: 'paragraph',
        text: 'The useful exercise is to map each obligation to a layer you operate, so compliance becomes a property of the platform rather than a per-team checklist.',
      },
      {
        type: 'highlight',
        title: 'From obligation to control',
        items: [
          'Residency - private inference and RAG on approved corpora, with retention and deletion controls.',
          'Transparency and documentation - pinned model versions, model cards, and request and response logging.',
          'Human oversight - gateway policy, approval workflows and scoped access.',
          'Accountability - identity, RBAC, and audited membership and ACL changes.',
          'Security and incident response - guardrails at the gateway, secrets management and a monitored boundary.',
          'Evidence - immutable audit logs and usage attribution by team and use case.',
        ],
      },
      {
        type: 'heading',
        text: 'The bottom line',
      },
      {
        type: 'paragraph',
        text: 'Self-hosting is close to a necessary condition for sovereign, regulated AI: it is what makes residency, provenance, auditability and continuity achievable rather than contractual promises. It is not sufficient. The differentiator is the governance layer you run on top of it - and the test is simple. Can you answer, on demand, which model handled which data, for which user, producing which output, under whose authority? Private GPUs alone will not satisfy a regulator. A governed private platform will.',
      },
    ],
    cta: {
      label: 'Talk to us about regulated AI infrastructure',
      href: 'https://cloud.ametnes.com/contact/',
    },
  },
  {
    slug: 'headroom-ai-self-host-ametnes',
    title: 'AI OSS spotlight: Headroom AI, and how to self-host it on Ametnes',
    category: 'AI Infrastructure',
    breadcrumbLabel: 'AI Infrastructure',
    publishedAt: '2026-08-02',
    displayDate: 'August 2, 2026',
    cardFooterLabel: 'Read the spotlight',
    excerpt:
      'Headroom AI is an Apache-2.0 context compression layer that strips 20-95% of the tokens your coding agents send - tool output, logs, files, RAG chunks and conversation history - without changing the answers. It wraps the developer tools your teams already use, and Ametnes runs it in your own environment on the free tier.',
    seoDescription:
      'Headroom AI open-source spotlight: benchmark stats, coding-agent integrations, why enterprise rollout needs a proxy layer and careful tuning, and how to self-host Headroom 0.36 on the Ametnes free tier with bring-your-own-infrastructure - plus curl, Python, JavaScript and Java examples.',
    blocks: [
      {
        type: 'lead',
        text: 'Headroom AI is one of those open-source projects that spreads through a developer team in a week. It is an Apache-2.0 context compression layer from [Headroom Labs](https://github.com/headroomlabs-ai/headroom) that sits between your coding agent and the model, compresses everything the agent reads - tool output, logs, files, RAG chunks and conversation history - and sends the model a fraction of the tokens for the same answer. It has passed 73,000 GitHub stars and was Trendshift\'s number one repository of the day, which tells you how much pain it removes. This spotlight covers why it works, the numbers behind it, where it fits in developer workflow, what changes when you roll it out across an enterprise, and how to self-host it on Ametnes - where it is part of the free tier.',
      },
      {
        type: 'heading',
        text: 'Why Headroom is spreading through developer teams',
      },
      {
        type: 'paragraph',
        text: 'The pitch is simple: a large share of what an AI agent reads is structurally redundant. JSON tool responses repeat field names thousands of times, log dumps are 99% noise around the one line that matters, and code exploration returns the same imports and boilerplate over and over. Headroom detects the content type and routes it to the right compressor - SmartCrusher for JSON, an AST-aware CodeCompressor for source code, and the Kompress-v2-base model for prose - then keeps the original locally so the model can retrieve it on demand.',
      },
      {
        type: 'paragraph',
        text: 'None of this is theoretical. Headroom\'s published benchmarks, reproducible from the repository with a fixed seed, show real reductions on the workloads agents hit every day.',
      },
      {
        type: 'highlight',
        title: 'Headroom by the numbers',
        items: [
          '73,000+ GitHub stars and Trendshift #1 repository of the day',
          '90%+ on repetitive JSON and log payloads; far less on dense prose',
          '0.21 ms p50 compression on a 10K-token JSON result - invisible in agent latency',
          'No accuracy tax: GSM8K unchanged at 0.870, TruthfulQA 0.530 → 0.560, and 97% tool-call accuracy at 32% compression',
        ],
      },
      {
        type: 'table',
        title: 'Measured savings on real agent workloads',
        columns: ['Workload', 'Before', 'After', 'Saved'],
        rows: [
          ['Code search (100 results)', '17,199', '13,597', '21%'],
          ['SRE incident debugging', '55,957', '24,340', '57%'],
          ['Codebase exploration', '58,801', '33,895', '42%'],
          ['GitHub issue triage', '46,067', '32,429', '30%'],
        ],
        source: 'Source: Headroom AI published benchmarks, [headroomlabs-ai/headroom](https://github.com/headroomlabs-ai/headroom) (Apache-2.0). Token counts measured with the provider tokenizer and the shipped `compress()`.',
      },
      {
        type: 'heading',
        text: 'The two economics of token compression',
      },
      {
        type: 'paragraph',
        text: 'Token savings mean different things depending on where the model runs - a line on a bill in one case, spare capacity in the other - and Headroom helps in both.',
      },
      {
        type: 'highlight',
        title: 'What the savings turn into',
        items: [
          'Public providers (Claude, OpenAI, DeepSeek) - every input and output token is billed, so compression is a direct cost reduction on the same workload, and smaller prompts also cut latency.',
          'Self-hosted LLMs - there is no per-token bill, but the context\'s KV cache lives in GPU memory. Fewer tokens per request means less VRAM held per conversation, so one GPU serves more concurrent requests and longer contexts.',
          'Throughput, not just cost - shorter prompts cut prefill compute, so time-to-first-token drops and the same hardware handles more requests per minute without buying more GPUs.',
          'Run it in the proxy layer - Headroom\'s compressors run on CPU by default, so putting it in front of the model compresses context before it ever reaches the LLM: the GPU only sees the smaller prompt and never competes for VRAM.',
          'Protect the prefix cache - over-compressing can bust provider prefix caching; Headroom\'s default cache-mode profiles compress only the newest delta to keep those hits.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Either way the outcome is the same shape: you spend less on hosted tokens, and you get more useful work out of the GPUs you already own. That is why the right place for Headroom is the proxy layer - compress once, on the way in, so the model only ever processes the smaller context.',
      },
      {
        type: 'heading',
        text: 'It wraps the tools your developers already use',
      },
      {
        type: 'paragraph',
        text: 'Headroom is not another chat UI to adopt. It is a drop-in layer that wraps the tools already on developer machines. A single `headroom wrap <tool>` command starts a local proxy and configures the agent to route through it - Claude Code, Codex, Cursor, Copilot CLI, VS Code Copilot, Aider, OpenCode, Cline, Continue, Goose, OpenHands, Kimi CLI and more, with `headroom unwrap <tool>` to undo it. Anything that speaks the OpenAI-compatible API works through `headroom proxy`, and any MCP client can call the built-in `headroom_compress`, `headroom_retrieve` and `headroom_stats` tools.',
      },
      {
        type: 'highlight',
        title: 'One compression layer, every entry point',
        items: [
          'Library - call `compress(messages)` inline in Python or TypeScript',
          'Proxy - `headroom proxy --port 8787` with zero code changes, any language',
          'Agent wrap - one command configures Claude Code, Codex, Cursor, Copilot, Aider and others',
          'MCP server - compression and retrieval tools for any MCP-native client',
          'Framework adapters - LiteLLM, LangChain, Agno, Strands, Vercel AI SDK, ASGI middleware',
        ],
      },
      {
        type: 'paragraph',
        text: 'Beyond the prompt, Headroom also trims what the model writes back. Verbosity steering and effort routing cut output tokens - output costs up to 5× input on Opus-class models - and report roughly 32% fewer output tokens with a confidence range rather than a made-up exact figure. Cross-agent memory gives Claude, Codex, Gemini and Grok one shared store with automatic dedup, and `headroom learn` mines failed sessions and writes corrections into `CLAUDE.local.md`, `AGENTS.md` or `GEMINI.md`.',
      },
      {
        type: 'heading',
        text: 'Why enterprise rollout is a different job',
      },
      {
        type: 'paragraph',
        text: 'Everything above is designed for an individual developer on a laptop: run it locally, start saving in minutes, and no prompt or file content leaves the machine to be compressed. Rolling it out across an engineering organisation is a different problem. You need an always-on shared deployment, centralised configuration and version rollout, org-wide savings dashboards, SSO and access control, and a supported path for VPC, on-premise and air-gapped installs. The practical answer is to treat Headroom as part of your proxy layer - the same place you already run routing, guardrails and spend controls - rather than a utility installed on each developer\'s machine.',
      },
      {
        type: 'paragraph',
        text: 'The second half of enterprise success is tuning the compression to your workloads instead of accepting defaults. Headroom ships savings profiles (`coding` by default, plus `balanced`, `agent-90` and `general`), a `HEADROOM_MIN_TOKENS` floor that leaves small or already-dense blocks byte-identical, and live-zone compression that keeps the frozen prompt prefix cache-friendly. Get this right and the savings are predictable; skip it and you either compress too little or cross the line where answer quality starts to move.',
      },
      {
        type: 'heading',
        text: 'Deploying Headroom on Ametnes',
      },
      {
        type: 'paragraph',
        text: 'Headroom is in the Ametnes catalogue as a managed service, and it is part of the Freemium tier, so there is no platform licence cost for the service itself. Ametnes runs on a bring-your-own-infrastructure (BYOI) model: you bring the environment, and the only real cost is the infrastructure Headroom runs on. Deploy it from the console like any other service - pick Headroom 0.36, choose an architecture, choose the location, and Ametnes provisions it in your private network with TLS, upgrades and patching handled.',
      },
      {
        type: 'paragraph',
        text: 'When the service is ready, the console gives you the connection details that matter: the endpoint host and port, and the API key clients use to reach the proxy. That endpoint is the single URL you point your agents, SDKs and CI jobs at. Headroom forwards to whichever model provider you configure behind it, so no prompt or file content leaves your environment to be compressed.',
      },
      {
        type: 'highlight',
        title: 'Why teams start on the free tier',
        items: [
          'Headroom is included in the Ametnes Freemium tier - there is no platform licence cost for the service',
          'BYOI: the only real cost is the infrastructure you run it on',
          'Deployed and operated inside your own VPC, on-premise or air-gapped environment',
          'Managed TLS, version pinning, patching and backups are part of day-2 operations',
        ],
      },
      {
        type: 'heading',
        text: 'Key configuration',
      },
      {
        type: 'paragraph',
        text: 'Configuration splits cleanly in two: what clients need, and what the Headroom service needs. When you point at an Ametnes-hosted endpoint the client surface is deliberately tiny - a base URL, plus the provider key you already use - while the provider, profile and output controls live on the service itself. If you already run LiteLLM as your gateway, you can also register Headroom as a `pre_call` guardrail so every request is compressed before it reaches the upstream provider, without changing gateway config.',
      },
      {
        type: 'highlight',
        title: 'Configuration that matters in production',
        items: [
          'Client: endpoint URL - the `host:port` from the Ametnes service connection details; point every client at it',
          'Client: provider API key - Headroom forwards your real OpenAI or Anthropic key to the provider, so keep using it unchanged',
          'Service: upstream provider - the base URL and key for the model provider Headroom forwards to (Anthropic, OpenAI, Bedrock or a compatible gateway)',
          'Service: compression profile - `HEADROOM_SAVINGS_PROFILE` selects `coding` (default, ~50%), `balanced` (~70%), `agent-90` (~90%) or `general`; `HEADROOM_MIN_TOKENS` leaves small blocks untouched',
          'Service: output shaping - `HEADROOM_OUTPUT_SHAPER=1` trims what the model writes back, proxy-side',
          'Service: retention - CCR keeps originals retrievable locally for a configurable TTL',
        ],
      },
      {
        type: 'paragraph',
        text: 'The tuning switches - provider, profile, output shaping and the anonymous beacon - are read by the proxy process, not by the applications that call it. There is nothing to export on a developer laptop that points at an Ametnes host. When you run `headroom proxy` yourself, they come from that process\'s environment, and a shared proxy hot-syncs changes from `headroom wrap` over `POST /admin/runtime-env`.',
      },
      {
        type: 'code',
        label: 'Client environment - all an app needs to reach Ametnes-hosted Headroom',
        code: [
          'export HEADROOM_URL="https://<host>:<port>"     # from the Ametnes connection details',
          'export OPENAI_API_KEY="<your-openai-key>"       # Headroom forwards this to the provider',
          '',
          '# Ametnes basic auth gates the /stats and /dashboard endpoints only',
          'export HEADROOM_USER="<basic-auth-user>"',
          'export HEADROOM_PASSWORD="<basic-auth-password>"',
        ].join('\n'),
      },
      {
        type: 'title',
        text: 'Integrating into your enterprise workflows',
      },
      {
        type: 'paragraph',
        text: 'Headroom meets your stack where it already is: an OpenAI-compatible proxy for applications, a one-command wrapper for coding agents, and a drop-in endpoint for the business applications your teams already run. Most integrations are a URL change rather than new code, and everything keeps flowing through the endpoint in your environment.',
      },
      {
        type: 'subheading',
        text: 'Integrating into applications',
      },
      {
        type: 'paragraph',
        text: 'For application code you have two options: compress inline with the library, or leave the code untouched and point your existing provider SDK at the Headroom endpoint. The proxy is OpenAI-compatible, so the same pattern works from curl, Python, JavaScript, Java or anything else that can set a base URL.',
      },
      {
        type: 'code',
        label: 'curl - an OpenAI-compatible request through the proxy',
        code: [
          'curl "$HEADROOM_URL/v1/chat/completions" \\',
          '  -H "Authorization: Bearer $OPENAI_API_KEY" \\',
          '  -H "Content-Type: application/json" \\',
          "  -d '{",
          '    "model": "gpt-4o",',
          '    "messages": [',
          '      {"role": "user", "content": "Summarise these logs and find the FATAL line"}',
          '    ]',
          "  }'",
        ].join('\n'),
      },
      {
        type: 'code',
        label: 'Python - compress inline with the library',
        code: [
          'import os',
          '',
          'from headroom import compress',
          'from openai import OpenAI',
          '',
          'messages = [{"role": "user", "content": "Analyze these results"}]',
          'result = compress(messages, model="gpt-4o")',
          '',
          'client = OpenAI()',
          'response = client.chat.completions.create(model="gpt-4o", messages=result.messages)',
          'print(f"Saved {result.tokens_saved} tokens ({result.compression_ratio:.0%})")',
          '',
          '# Proxy-only alternative - skip compress() and point the SDK at Headroom:',
          '# client = OpenAI(base_url=os.environ["HEADROOM_URL"])   # OPENAI_API_KEY is forwarded',
        ].join('\n'),
      },
      {
        type: 'code',
        label: 'JavaScript / TypeScript - library or proxied client',
        code: [
          "import { compress } from 'headroom-ai';",
          '',
          "const messages = [{ role: 'user', content: 'Summarise these logs' }];",
          "const result = await compress(messages, { model: 'gpt-4o' });",
          '',
          '// Or route an OpenAI-compatible client through the proxy:',
          'const client = new OpenAI({',
          '  baseURL: process.env.HEADROOM_URL,',
          '  apiKey: process.env.OPENAI_API_KEY,',
          '});',
          '',
          '// Vercel AI SDK: wrapLanguageModel({ model, middleware: headroomMiddleware() })',
        ].join('\n'),
      },
      {
        type: 'code',
        label: 'Java - any OpenAI-compatible client via the proxy (Java 15+)',
        code: [
          'import java.net.URI;',
          'import java.net.http.HttpClient;',
          'import java.net.http.HttpRequest;',
          'import java.net.http.HttpResponse;',
          '',
          'var body = """',
          '    {',
          '      "model": "gpt-4o",',
          '      "messages": [{"role": "user", "content": "Summarise these logs"}]',
          '    }',
          '    """;',
          '',
          'var request = HttpRequest.newBuilder()',
          '    .uri(URI.create(System.getenv("HEADROOM_URL") + "/v1/chat/completions"))',
          '    .header("Authorization", "Bearer " + System.getenv("OPENAI_API_KEY"))',
          '    .header("Content-Type", "application/json")',
          '    .POST(HttpRequest.BodyPublishers.ofString(body))',
          '    .build();',
          '',
          'var response = HttpClient.newHttpClient()',
          '    .send(request, HttpResponse.BodyHandlers.ofString());',
          '',
          'System.out.println(response.body());',
        ].join('\n'),
      },
      {
        type: 'subheading',
        text: 'Integrating into coding agents',
      },
      {
        type: 'paragraph',
        text: 'Coding agents are where Headroom started, and where it asks the least of you. `headroom wrap <tool>` starts the local proxy, installs Serena for semantic code navigation, and launches the agent already configured to route through Headroom - Claude Code, Codex, Grok CLI, Copilot CLI, VS Code Copilot, Aider, OpenCode, Cline, Continue, Goose, OpenHands and Kimi CLI are all covered, and `headroom unwrap <tool>` restores the settings that existed before. Agents on the same proxy also share one cross-agent memory store with automatic dedup, and `headroom learn` mines failed sessions into your `CLAUDE.local.md`, `AGENTS.md` or `GEMINI.md`.',
      },
      {
        type: 'code',
        label: 'Wrap a coding agent, or install the MCP server',
        code: [
          '# Wrap a coding agent - starts the proxy and configures the tool',
          'headroom wrap claude',
          'headroom wrap codex',
          'headroom wrap cursor',
          '',
          '# Install the MCP server for MCP-native clients',
          'headroom mcp install',
          '',
          '# Undo a durable wrap',
          'headroom unwrap claude',
        ].join('\n'),
      },
      {
        type: 'subheading',
        text: 'Integrating into business applications',
      },
      {
        type: 'paragraph',
        text: 'Any business application that lets you set an OpenAI-compatible base URL can route through Headroom - Open WebUI, LibreChat, Dify, AnythingLLM and similar tools included. Point the app\'s OpenAI connection at the Headroom endpoint and its RAG chunks, long conversations and tool payloads are compressed before they reach the model provider. No application code changes, and the app keeps using the provider key it already has - Headroom forwards it upstream.',
      },
      {
        type: 'code',
        label: 'Open WebUI - set the OpenAI connection to the Headroom endpoint',
        code: [
          '# Open WebUI',
          'OPENAI_API_BASE_URL=$HEADROOM_URL/v1',
          'OPENAI_API_KEY=<your-openai-key>   # forwarded by Headroom to the provider',
          '',
          '# Other OpenAI-compatible apps: set the same base URL in their',
          '# connection settings; the setting name varies by application.',
        ].join('\n'),
      },
      {
        type: 'heading',
        text: 'Verify compression',
      },
      {
        type: 'paragraph',
        text: 'Once traffic is flowing, confirm Headroom is actually saving tokens. The proxy exposes live stats, and Ametnes secures the stats and dashboard endpoints with basic auth - use the credentials from the service connection details. Swap in your own host and port, and only drop the `-u` flag if your deployment is unauthenticated.',
      },
      {
        type: 'code',
        label: 'Check live compression stats',
        code: [
          'curl -s -u "$HEADROOM_USER:$HEADROOM_PASSWORD" \\',
          '  "$HEADROOM_URL/stats" \\',
          "  | jq '{requests: .requests.total, saved: .tokens.saved, pct: .tokens.savings_percent, overhead_ms: .overhead.average_ms}'",
        ].join('\n'),
      },
      {
        type: 'paragraph',
        text: 'A healthy response shows requests and saved tokens climbing as traffic passes through, a savings percentage in the range you expect for the workload, and well under a millisecond of average overhead. If everything reads zero, check that local telemetry is enabled on the service (`HEADROOM_TELEMETRY=on` powers `/stats`, `/metrics` and the dashboard) and that clients are actually hitting the endpoint rather than the provider directly.',
      },
      {
        type: 'paragraph',
        text: 'The same numbers are available as a live UI at `$HEADROOM_URL/dashboard`, with hourly, daily and weekly rollups, per-model and per-client breakdowns, and cost avoided. On a managed Ametnes deployment this is the screen to watch in the first week - it tells you quickly whether the profile you chose fits the workload.',
      },
      {
        type: 'heading',
        text: 'Tuning it for teams, not just laptops',
      },
      {
        type: 'paragraph',
        text: 'Once Headroom is centralised, use it the way platform teams use any shared proxy: pin the version, roll configuration centrally, watch `headroom dashboard` and `headroom savings` for org-wide results, and use `headroom learn --verbosity` to pick a terseness level from real sessions instead of guessing. Start with the balanced profile, measure with a holdout, then tune per workload - high-volume tool traffic, RAG pipelines and coding agents each have a different sweet spot. That is the difference between a demo that saves tokens and a compression layer your engineers trust.',
      },
      {
        type: 'paragraph',
        text: 'Headroom is a rare open-source project that delivers immediate individual value and a clear enterprise return, and it is exactly the kind of workload Ametnes is built to operate: open source, running in your environment, on the free tier, with the day-2 operations handled.',
      },
    ],
    cta: {
      label: 'Talk to us about private AI infrastructure',
      href: 'https://cloud.ametnes.com/contact/',
    },
  },
  {
    slug: 'membership-management',
    title: 'Introducing membership management on the Ametnes platform',
    category: 'Platform',
    breadcrumbLabel: 'Platform',
    publishedAt: '2026-09-14',
    displayDate: 'September 14, 2026',
    cardFooterLabel: 'Read the post',
    excerpt:
      'Invite teammates into your Ametnes workspace, grant scoped access to projects, resources and locations, and manage every membership from one place - with pending invitations, expiry, deny-by-default access and an audit trail.',
    seoDescription:
      'Membership management on the Ametnes platform for solo developers and enterprise organisation accounts: invite members by email, accept or decline invitations, scope access with ACLs across projects, resources and locations, and manage the full membership lifecycle across workspaces.',
    blocks: [
      {
        type: 'lead',
        text: 'Ametnes accounts have always had owners; now they have teams. Membership management is live on the platform. You can invite teammates into your workspace by email, scope exactly what they can see and change, and let them move between the workspaces they belong to - without sharing credentials or building a parallel access model outside the platform.',
      },
      {
        type: 'heading',
        text: 'Every user starts with a workspace',
      },
      {
        type: 'paragraph',
        text: 'When someone signs up for Ametnes they get their own personal workspace, and they are the owner of it. Membership management lets that owner invite other people into the same workspace, and lets each user belong to several workspaces at once - their own, plus any they have been invited to. Privileges are tied to the membership, not to a shared login, so access ends when the membership does.',
      },
      {
        type: 'heading',
        text: 'Invite, accept, decline, leave',
      },
      {
        type: 'paragraph',
        text: 'The owner invites a teammate by email. If that email already has an Ametnes account the invitation is attached to it; if not, the platform creates the user first so the membership always resolves to a real person rather than a dangling invite. The invitation is pending until the recipient accepts or declines it, and pending invitations expire after seven days so stale access does not linger. A revoked or expired membership can be re-issued later instead of piling up duplicates.',
      },
      {
        type: 'highlight',
        title: 'The membership lifecycle',
        items: [
          'Pending - invited, waiting for the recipient to accept or decline (expires after 7 days)',
          'Active - the member belongs to the workspace and can switch into it',
          'Revoked - the member left or was removed; access is withdrawn immediately',
          'Expired - a pending invitation that was never accepted in time',
        ],
      },
      {
        type: 'heading',
        text: 'Access is scoped, and denied by default',
      },
      {
        type: 'paragraph',
        text: 'Membership is not an all-or-nothing switch. Each membership carries a set of access-control entries, and those entries can only target projects, resources and locations - the platform ignores anything else, so a membership can never be used to widen access sideways. A member of a workspace that is not their own is never treated as a root user; they can act only where the membership explicitly grants it, and everything else is denied by default. The owner of the workspace keeps their root status.',
      },
      {
        type: 'highlight',
        title: 'What a membership can grant',
        items: [
          'Projects - the workspace groupings a member can work in',
          'Resources - specific services the member can view or operate',
          'Locations - the data service locations they can deploy into',
        ],
      },
      {
        type: 'heading',
        text: 'What the workspace owner manages',
      },
      {
        type: 'paragraph',
        text: 'Membership management is deliberately owner-only. The workspace owner lists active members, invites new ones, updates a member\'s access as their role changes, and removes them when they leave. Members can accept, decline or leave a workspace themselves, but they cannot remove their own membership on their home account - the workspace you own always stays yours. This keeps a single, accountable path for who gets access and who takes it away.',
      },
      {
        type: 'heading',
        text: 'Every change is audited',
      },
      {
        type: 'paragraph',
        text: 'Invites, acceptances and departures are written to the audit log, so "who added this person, and when did their access end?" has an answer. Combined with revoking access in one place, that turns offboarding into a single action instead of a scavenger hunt across shared logins and scattered credentials.',
      },
      {
        type: 'heading',
        text: 'One model for solo developers and enterprise orgs',
      },
      {
        type: 'paragraph',
        text: 'The same membership model scales from one person to a whole organisation, with no separate "enterprise edition" of access control. A solo developer on the free tier invites their first collaborator with the same invite-and-scope flow an enterprise uses for hundreds of people.',
      },
      {
        type: 'paragraph',
        text: 'For an enterprise, the workspace is the organisation account and memberships are the people in it. Onboarding is an invite with scoped access rather than a shared login; business units and environments can be separated into their own workspaces; the owner stays accountable for every change; and each invite, acceptance and departure is written to the audit log, so offboarding is a single revoke instead of a scavenger hunt.',
      },
      {
        type: 'highlight',
        title: 'Why enterprises standardise on it',
        items: [
          'One organisation account - every member, team and environment under a single governed structure.',
          'Scoped by default - access is granted per project, resource and location, and denied everywhere else.',
          'Accountable ownership - only the workspace owner can invite, update or remove members.',
          'Auditable lifecycle - pending, active, revoked and expired states, with history for every change.',
          'Start free - the same model is available on the free tier, so teams can adopt it before they scale.',
        ],
      },
      {
        type: 'heading',
        text: 'Managing memberships in the console',
      },
      {
        type: 'paragraph',
        text: 'Everything is done in the Ametnes console, so whoever manages your team does not need to touch an API. The flow is short.',
      },
      {
        type: 'highlight',
        title: 'Invite, review and remove members',
        items: [
          'Invite - open the workspace you own, go to `Users`, click `Invite`, enter one or more email addresses, then click `Send Invites`.',
          'Review - new members appear in the `Users` list with a `Pending` status until they respond; use the status filter (All, Pending, Active, Inactive) to see who has joined.',
          'Set access - open a member from the `Users` list and use the `Access Control Lists` tab to grant access to specific projects, resources and locations.',
          'Remove - choose `Remove from workspace` on the member row; access is withdrawn immediately.',
        ],
      },
      {
        type: 'highlight',
        title: 'Accept, decline or leave a workspace',
        items: [
          'Open your own profile in the console.',
          'Go to the `Memberships` tab to see invitations and the workspaces you belong to.',
          'Click `Accept` to join or `Decline` to refuse an invitation.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Membership management is available on every plan, including the free tier, so a solo developer can invite the first collaborator without upgrading and an enterprise can run its whole organisation account on the same model. The full walkthrough - states, scopes, and the console steps - is in the [membership management documentation](/docs/user-guide/platform/membership-management/).',
      },
    ],
    cta: {
      label: 'Create a free workspace',
      href: 'https://cloud.ametnes.com/app/signup',
    },
  },
  {
    slug: 'the-leapfrog-moment-for-emerging-economies',
    title: 'The Leapfrog Moment: Why AI Changes the Math for Emerging Economies',
    category: 'AI Infrastructure',
    breadcrumbLabel: 'AI Infrastructure',
    publishedAt: '2026-07-13',
    displayDate: 'July 13, 2026',
    cardFooterLabel: 'Read the post',
    excerpt:
      'When the UK built GOV.UK it replaced 1,900 government websites and saved billions. AI makes that kind of leapfrog accessible to countries that would never have had that budget before.',
    seoDescription:
      'AI enables leapfrog citizen services for emerging economies - GOV.UK saved billions without AI, and the opportunity is even bigger now. Sovereign AI infrastructure, and why political will matters more than technology.',
    blocks: [
      {
        type: 'lead',
        text: 'AI is one of the biggest accelerants emerging economies have ever had access to - a chance to deliver citizen services that would otherwise have taken decades and billions to build. The cost of building no longer requires the budgets it once did, and the precedent is already there.',
      },
      {
        type: 'heading',
        text: 'The GOV.UK precedent',
      },
      {
        type: 'paragraph',
        text: "Look at GOV.UK. When it launched, it replaced nearly 1,900 separate government websites with a single platform for passports, tax, vehicle registration, education, and more. It launched at less than 30% of the annual cost of the services it replaced, saved taxpayers an estimated £70 million a year, and contributed to more than £4 billion in savings within its first four years.",
      },
      {
        type: 'paragraph',
        text: "That was built over a decade ago, without AI. It's not a stretch to imagine a similar - or better - platform being built today, faster and cheaper, in a country with a fraction of the UK's budget.",
      },
      {
        type: 'heading',
        text: 'The opportunity goes beyond digitizing what already exists',
      },
      {
        type: 'paragraph',
        text: 'Many countries in Africa and parts of the Caribbean still do not have a formal addressing system. Urban planning, road maintenance, waste and drainage management, housing planning, agriculture, education, financial inclusion, healthcare, and pension services all carry the scars of decades of underinvestment and disconnected systems.',
      },
      {
        type: 'paragraph',
        text: 'AI now offers a way to leapfrog - not by copying what wealthier nations built, but by designing something better, natively, without carrying the same technical debt.',
      },
      {
        type: 'heading',
        text: 'The constraint is not technology. It is ambition - but ambition alone is not enough.',
      },
      {
        type: 'paragraph',
        text: 'The real question is whether political leaders have the vision and the will to seize this moment. The tools exist. The cost of building no longer requires the budgets it once did.',
      },
      {
        type: 'paragraph',
        text: 'That said, vision has to be matched with execution, and the non-technical barriers are real. Digital literacy, reliable electricity and internet access, and regulatory frameworks that have not caught up to the technology all still stand in the way. There is also a subtler risk: leapfrogging on AI could mean trading old dependencies for new ones, if the underlying platforms and infrastructure are entirely foreign-owned and foreign-hosted. Political will has to be paired with local capacity to govern, maintain, and eventually own these systems - otherwise the leap forward comes with strings attached.',
      },
      {
        type: 'highlight',
        title: 'What sovereign AI means in practice',
        items: [
          'Models served on infrastructure you govern, behind your network boundary',
          'Policy enforcement at the gateway: PII controls, classification-based routing, and audit logs you control',
          'The ability to keep inference private when geopolitics or regulation makes hosted access unreliable',
        ],
      },
      {
        type: 'paragraph',
        text: 'None of that is a reason for inaction. It is a case for building with intent - sovereignty and capability by design, not an afterthought. There is no longer a credible excuse to point at external forces or historical imperialism as the reason progress is slow. The opportunity is sitting right in front of the countries that choose to act; the work now is doing it right, not just doing it fast.',
      },
      {
        type: 'paragraph',
        text: 'If this resonates, join our upcoming webinar on sovereign AI - we are walking through a practical open-source architecture for governments and regulated industries that need to keep AI infrastructure under their own control. Register for Sovereign AI in Private Environments on 29 July 2026.',
      },
    ],
    cta: {
      label: 'Register for the sovereign AI webinar',
      href: '/events/sovereign-ai-in-private-environments',
    },
  },
  {
    slug: 'litellm-rust-migration-ai-gateway',
    title: 'LiteLLM is moving to Rust',
    category: 'AI Infrastructure',
    breadcrumbLabel: 'AI Infrastructure',
    publishedAt: '2026-06-23',
    displayDate: 'June 23, 2026',
    cardFooterLabel: 'Read the analysis',
    excerpt:
      'LiteLLM is moving its AI gateway to Rust - welcome news for teams who have been frustrated by its performance under load. Memory spikes, OOM kills, and gateway overhead have made scaling painful.',
    seoDescription:
      'Analysis of LiteLLM migrating its AI gateway to Rust: Python proxy memory and latency limits, benchmark results, staged migration plan, and what it means for teams running private LiteLLM in their own environments.',
    blocks: [
      {
        type: 'lead',
        text: 'On 22 June 2026, LiteLLM published a detailed plan to migrate its AI gateway to Rust - targeting sub-1ms per-request overhead and a sub-100MB deployment footprint. The motivation is not cosmetic. Under real concurrent load, the Python-based proxy peaks around 359MB of memory, adds roughly 7.5ms of gateway overhead per request, and has left production teams fighting OOM kills at the worst possible moment. For Ametnes customers who have long been frustrated by LiteLLM under load, this is genuinely exciting news: the gateway they already run is finally getting a runtime built to scale.',
      },
      {
        type: 'heading',
        text: 'What customers have been running into',
      },
      {
        type: 'paragraph',
        text: 'LiteLLM is the most widely adopted open-source AI gateway for a reason: one OpenAI-compatible API surface across 100+ providers, with routing, fallbacks, rate limits, spend tracking, and guardrails teams can run in their own environment. That model fits regulated and cost-conscious enterprises that want model choice without surrendering control to a SaaS control plane.',
      },
      {
        type: 'paragraph',
        text: 'The friction shows up when traffic scales. Gateway instances that look healthy at low concurrency climb in CPU and resident memory as parallel requests stack up. Teams sizing capacity for a lightweight proxy discover they need several hundred megabytes per instance - and still watch processes restart under burst load. Per-request overhead in the single-digit millisecond range sounds small until you are routing embeddings, classification, or agent tool calls at thousands of requests per second; the gateway becomes a tax on every call.',
      },
      {
        type: 'highlight',
        title: 'Symptoms we see in private AI gateway deployments',
        items: [
          'Memory use that grows with concurrency, forcing larger instances and higher infrastructure cost',
          'OOM kills during traffic spikes - often when scaling has not caught up',
          'Gateway latency overhead that dominates for fast, high-volume workloads like embeddings',
          'More capacity required to hit throughput targets, multiplying memory and licensing cost',
          'Operational toil tuning worker counts and concurrency instead of focusing on model policy',
        ],
      },
      {
        type: 'heading',
        text: 'What LiteLLM measured',
      },
      {
        type: 'paragraph',
        text: 'LiteLLM [published](https://docs.litellm.ai/blog/litellm-rust-launch) the migration plan and benchmark methodology on 22 June 2026. The harness and summarized results are checked into their repository so teams can reproduce the sub-1ms overhead claim independently.',
      },
      {
        type: 'paragraph',
        text: 'In LiteLLM’s published benchmark against the same mock upstream workload, the Rust gateway path served roughly 15× the throughput of the Python proxy (about 6,782 vs 453 requests per second at 50 concurrent clients) while using about 11× less peak memory (about 32MB vs 359MB). Per-request gateway overhead dropped from about 7.5ms on the Python path to about 0.05ms on Rust - well under the sub-1ms target LiteLLM is committing to publicly.',
      },
      {
        type: 'paragraph',
        text: 'Those numbers measure forwarding, transformation, and streaming on the hot path - not full model latency. For workloads where the LLM itself is fast, or where you fan out many small calls, the gateway stops being negligible infrastructure and starts shaping tail latency and fleet size.',
      },
      {
        type: 'heading',
        text: 'What this means if you run LiteLLM privately',
      },
      {
        type: 'paragraph',
        text: 'Teams chose LiteLLM for its provider breadth and enterprise features - the frustration has always been performance under load, not capability. LiteLLM’s Rust migration directly addresses that gap.',
      },
      {
        type: 'paragraph',
        text: 'Multiple Ametnes customers running LiteLLM in private environments had started evaluating a move to Bifrost - not because they wanted to abandon LiteLLM’s provider coverage or operational model, but because the Python proxy could not keep up with their throughput and memory targets. LiteLLM’s Rust announcement changes that calculus: teams can keep the gateway they already know, with the config, integrations, and runbooks they have built, and expect performance in the same class as purpose-built compiled gateways.',
      },
      {
        type: 'paragraph',
        text: 'The Rust work addresses the consequences of running that feature surface on a runtime that was never optimized for high-concurrency, low-overhead forwarding - consequences many of LiteLLM’s own customers reported before this announcement.',
      },
      {
        type: 'heading',
        text: 'Operating the gateway layer in environments you control',
      },
      {
        type: 'paragraph',
        text: 'Ametnes helps teams deploy and operate private AI infrastructure - including LiteLLM and complementary gateways - in VPC, on-premise, and hybrid estates. That means capacity planning, ingress and TLS, upgrades as the Rust migration ships, observability on gateway spend and latency, and day-2 operations so the routing layer stays as reliable as the models behind it.',
      },
      {
        type: 'paragraph',
        text: 'If you are evaluating whether to run LiteLLM at production scale privately, or you are already hitting memory and throughput ceilings on the Python proxy, the migration timeline is worth tracking now - not after the next OOM incident.',
      },
    ],
    cta: {
      label: 'Talk to us about private AI gateways',
      href: 'https://cloud.ametnes.com/contact/',
    },
  },
  {
    slug: 'vault-secrets-control-lane',
    title: 'Private secrets management with Vault: a practical guide',
    category: 'Security',
    breadcrumbLabel: 'Security',
    publishedAt: '2026-06-10',
    displayDate: 'June 10, 2026',
    cardFooterLabel: 'Read the guide',
    excerpt:
      'This guide covers the private secrets management patterns teams adopt first - dynamic database credentials, KV v2 API key rotation, short-lived PKI certificates, and CI integration - with GitLab as one worked example.',
    seoDescription:
      'Practical guide to private secrets management with HashiCorp Vault: dynamic database credentials, KV v2 rotation, short-lived PKI, and CI integration - with GitLab job JWTs as a worked example.',
    blocks: [
      {
        type: 'lead',
        text: 'If your goal is to store a handful of static secrets and inject them into CI jobs, GitLab CI/CD variables are good enough. The gap shows up when you need unique credentials per pipeline run, credentials that expire on their own, internal TLS certificates issued on demand, and audit logs that answer "who read prod/db at 03:12?" This guide walks through the Vault patterns behind GitLab - with commands and pipeline config you can adapt directly.',
      },
      {
        type: 'heading',
        text: 'Prerequisites',
      },
      {
        type: 'paragraph',
        text: 'You need a running Vault cluster with JWT auth, database engine, KV v2, and PKI pre-configured. The examples below use `vault.prod-accounts.ametnes.net` and `sql.prod-accounts.ametnes.net`, and assume GitLab mints job JWTs validated via JWKS at `${GITLAB_URL}/oauth/discovery/keys`. Set your shell variables before running the CLI steps:',
      },
      {
        type: 'code',
        label: 'Environment variables',
        code: `export VAULT_ADDR="https://vault.prod-accounts.ametnes.net"
export VAULT_TOKEN="<admin-token>"          # named admin policy, not root
export GITLAB_URL="https://gitlab.com"
export DEMO_PG_HOST="sql.prod-accounts.ametnes.net"
export DEMO_PROJECT_PATH="acme/platform-api"  # GitLab project path for JWT binding`,
      },
      {
        type: 'heading',
        text: 'Delivery channel vs control plane',
      },
      {
        type: 'paragraph',
        text: 'GitLab is the delivery channel for secrets. Vault is the secrets control plane. You do not rip out GitLab - you wire Vault behind it for what variables cannot do. Most of that capability is open-source Vault: database engine, PKI, KV v2, JWT auth, HA Raft, and audit devices.',
      },
      {
        type: 'highlight',
        title: 'What GitLab variables cannot do',
        items: [
          'Issue a unique database username and password for every CI job',
          'Automatically expire credentials when a job finishes',
          'Mint short-lived internal TLS certificates on demand',
          'Record path-level audit of which identity read which secret',
          'Version and roll back static API keys in one place',
        ],
      },
      {
        type: 'heading',
        text: 'Step 1 - Verify JWT auth (GitLab job tokens)',
      },
      {
        type: 'paragraph',
        text: 'Vault validates GitLab job JWTs against the JWKS endpoint - no static Vault token stored in GitLab project variables. Confirm the auth mount and inspect the role bindings before touching CI:',
      },
      {
        type: 'code',
        label: 'Inspect JWT auth mount',
        code: `vault auth list | grep jwt
vault read auth/jwt/config`,
      },
      {
        type: 'paragraph',
        text: 'Check that `jwks_url` points at `${GITLAB_URL}/oauth/discovery/keys` and `bound_issuer` matches your GitLab base URL. Then inspect the CI-bound role - bindings on `project_path`, `ref`, and `environment` restrict which pipelines can authenticate:',
      },
      {
        type: 'code',
        label: 'Inspect the CI role',
        code: `vault read auth/jwt/role/gitlab-deploy-prod`,
      },
      {
        type: 'paragraph',
        text: 'A short token TTL (10–15 minutes) and a least-privilege policy on the role are the production defaults. For local testing without a pipeline, exchange a job JWT manually:',
      },
      {
        type: 'code',
        label: 'Simulate JWT login from a test pipeline job',
        code: `export DEMO_JWT="<gitlab-job-jwt-from-pipeline>"
vault write -field=token auth/jwt/login role=gitlab-deploy-prod jwt="$DEMO_JWT"`,
      },
      {
        type: 'heading',
        text: 'Step 2 - GitLab CI pipeline',
      },
      {
        type: 'paragraph',
        text: 'Each job declares `id_tokens` so GitLab mints a per-job JWT. The `aud` claim must match what Vault expects - `https://vault.prod-accounts.ametnes.net` in this guide. The job logs in during `before_script`, then fetches secrets at runtime:',
      },
      {
        type: 'code',
        label: '.gitlab-ci.yml',
        code: `stages:
  - deploy

.deploy_vault_auth: &deploy_vault_auth
  id_tokens:
    VAULT_ID_TOKEN:
      aud: https://vault.prod-accounts.ametnes.net   # must match Vault bound_audiences
  before_script:
    - apk add --no-cache jq openssl
    - export VAULT_TOKEN="$(vault write -field=token auth/jwt/login role=gitlab-deploy-prod jwt="$VAULT_ID_TOKEN")"

deploy-staging:
  <<: *deploy_vault_auth
  stage: deploy
  image: hashicorp/vault:1.20
  variables:
    VAULT_ADDR: https://vault.prod-accounts.ametnes.net
  script:
    - vault kv get -field=api_key secret/integrations/demo-saas
    - vault read database/creds/demo-app-readonly
    - |
      vault write -format=json pki/issue/demo-web \\
        common_name="api.prod-accounts.ametnes.net" ttl="24h" alt_names="api.prod-accounts.ametnes.net" \\
        | jq -r '.data.certificate' | openssl x509 -noout -subject -dates`,
      },
      {
        type: 'paragraph',
        text: 'Contrast this with the older pattern: a long-lived `VAULT_TOKEN` or AppRole secret in a protected GitLab variable carries the same blast radius as any static credential. JWT auth gives you per-job identity with nothing stored in project settings.',
      },
      {
        type: 'heading',
        text: 'Step 3 - Dynamic database credentials',
      },
      {
        type: 'paragraph',
        text: 'The database secrets engine issues a unique username and password on every read. Confirm the engine is configured, then request credentials:',
      },
      {
        type: 'code',
        label: 'Read dynamic PostgreSQL credentials',
        code: `vault secrets list | grep database
vault read database/config/demo-postgresql
vault read database/creds/demo-app-readonly`,
      },
      {
        type: 'paragraph',
        text: 'The output includes a unique username (e.g. `v-token-demo-…`), a random password, `lease_id`, and `lease_duration`. Prove they work, then save the lease ID for the revoke demo:',
      },
      {
        type: 'code',
        label: 'Connect with issued credentials',
        code: `export DEMO_DB_USER="<username-from-output>"
export DEMO_DB_PASS="<password-from-output>"
export DEMO_LEASE_ID="<lease_id-from-output>"

psql "postgresql://\${DEMO_DB_USER}:\${DEMO_DB_PASS}@sql.prod-accounts.ametnes.net:5432/appdb?sslmode=disable" \\
  -c "SELECT current_user, now();"`,
      },
      {
        type: 'paragraph',
        text: 'Read again - Vault returns a different username and password. Revoke the first lease and the original credentials stop working immediately:',
      },
      {
        type: 'code',
        label: 'Revoke and verify expiry',
        code: `vault read database/creds/demo-app-readonly   # new creds - not reused
vault lease revoke "$DEMO_LEASE_ID"             # kill the first set
# retry psql with DEMO_DB_USER / DEMO_DB_PASS → connection fails`,
      },
      {
        type: 'paragraph',
        text: 'Rotation is TTL plus revocation, not a quarterly calendar reminder to update `DB_PASSWORD` in GitLab. The same pattern works inline in CI after JWT login:',
      },
      {
        type: 'code',
        label: 'Fetch DB creds inside a deploy job',
        code: `export VAULT_TOKEN="$(vault write -field=token auth/jwt/login role=gitlab-deploy-prod jwt="$VAULT_ID_TOKEN")"
export PGPASSWORD="$(vault read -field=password database/creds/demo-app-readonly)"
# run migrations or deploy using $PGPASSWORD`,
      },
      {
        type: 'heading',
        text: 'Step 4 - API keys in KV v2',
      },
      {
        type: 'paragraph',
        text: 'Third-party API keys cannot be made dynamic at the provider. KV v2 centralizes them with versioning - write, read latest, rotate by writing a new version, and pin a specific version during staged cutover:',
      },
      {
        type: 'code',
        label: 'Write, version, and rotate an API key',
        code: `vault kv put secret/integrations/demo-saas api_key="sk_demo_initial_001"
vault kv get secret/integrations/demo-saas
vault kv metadata get secret/integrations/demo-saas   # version 1

vault kv put secret/integrations/demo-saas api_key="sk_demo_rotated_002"
vault kv get secret/integrations/demo-saas              # latest → version 2
vault kv get -version=1 secret/integrations/demo-saas   # rollback path`,
      },
      {
        type: 'paragraph',
        text: 'Fetch a single field in CI without dumping the whole secret:',
      },
      {
        type: 'code',
        label: 'Read one field from KV in a pipeline',
        code: `vault kv get -field=api_key secret/integrations/demo-saas`,
      },
      {
        type: 'paragraph',
        text: 'Vault does not call Stripe API to invalidate the old key - your automation writes the new version, updates consumers, then revokes at the provider. Vault gives you versioning, access policy, and audit.',
      },
      {
        type: 'heading',
        text: 'Step 5 - PKI: short-lived certificates',
      },
      {
        type: 'paragraph',
        text: 'Vault PKI issues internal certificates with explicit TTL from the same pipeline that fetches database creds and API keys. Confirm the mount, then issue a 24-hour cert:',
      },
      {
        type: 'code',
        label: 'Issue and inspect a short-lived certificate',
        code: `vault secrets list | grep pki
vault list pki/roles

vault write -format=json pki/issue/demo-web \\
  common_name="api.prod-accounts.ametnes.net" \\
  ttl="24h" \\
  alt_names="api.prod-accounts.ametnes.net" \\
  | jq -r '.data.certificate' | openssl x509 -noout -subject -dates`,
      },
      {
        type: 'paragraph',
        text: 'For deploy steps that need files on disk, extract certificate and key from the JSON response:',
      },
      {
        type: 'code',
        label: 'Write TLS files in CI',
        code: `vault write -format=json pki/issue/demo-web \\
  common_name="api.prod-accounts.ametnes.net" ttl=24h > cert.json
jq -r '.data.certificate' cert.json > tls.crt
jq -r '.data.private_key' cert.json > tls.key`,
      },
      {
        type: 'heading',
        text: 'Running Vault reliably in your environment',
      },
      {
        type: 'paragraph',
        text: 'The blocker for many teams is not license cost - it is day-0 and day-2 operations. HA Raft, initialization, ingress, unseal after pod restarts, patching, and backups are real work. Ametnes deploys Vault in your private VPC, on-premise or private datacenter with that foundation handled, including optional transit auto-unseal so a pod restart does not require a Shamir quorum at 3am.',
      },
      {
        type: 'paragraph',
        text: 'OpenBao offers the same engine model under MPL 2.0 if BSL is a procurement concern. Infisical is a credible alternative when project-scoped UX and its own module APIs are enough. Ametnes supports Vault, OpenBao, and Infisical so the choice stays yours.',
      },
    ],
    cta: {
      label: 'Talk to us about private secrets management',
      href: 'https://cloud.ametnes.com/contact/',
    },
  },
  {
    slug: 'vercel-breach-private-secrets-lessons',
    title: "Vercel's breach: why production secrets don't belong in shared platforms",
    category: 'Security',
    breadcrumbLabel: 'Security',
    publishedAt: '2026-05-19',
    displayDate: 'May 19, 2026',
    cardFooterLabel: 'Read the analysis',
    excerpt:
      'A compromised third-party AI tool and a broad Google Workspace OAuth grant gave attackers a path from corporate identity to Vercel deployment secrets. The incident is a reminder that where you store credentials is a core architecture and compliance decision.',
    seoDescription:
      'Analysis of the April 2026 Vercel security incident: Context.ai OAuth compromise, exposed environment variables, and why teams should run private secrets management with Vault, OpenBao, or Infisical.',
    blocks: [
      {
        type: 'lead',
        text: 'In April 2026, Vercel disclosed unauthorized access to internal systems that began not in its core hosting platform, but in a third-party AI tool (Context.ai) connected to an employee Google Workspace account through OAuth. Attackers used that foothold to pivot into Vercel and read customer environment variables that were not marked as sensitive: API keys, database credentials, tokens, and similar production secrets stored alongside ordinary application config.',
      },
      {
        type: 'heading',
        text: 'How the attack chain worked',
      },
      {
        type: 'paragraph',
        text: 'The incident followed a familiar OAuth supply-chain pattern. Context.ai had been compromised earlier - reportedly through malware on an employee machine - giving attackers access to OAuth tokens for users of Context AI tooling. At least one Vercel employee had authorized Context.ai against their enterprise Google Workspace account with broad permissions. With a stolen token, the attacker impersonated that employee, moved from Google identity into Vercel internal systems, and reached the project settings interface where deployment environment variables are managed.',
      },
      {
        type: 'paragraph',
        text: 'Vercel stores environment variables encrypted at rest, but its sensitive designation is opt-in. Variables not flagged as sensitive remained readable through the management UI - exactly the class of values teams routinely treat as secrets in practice. Vercel advised customers to assume non-sensitive variables may have been exposed and rotate them, enable sensitive classification for secrets going forward, and audit OAuth applications connected to corporate Google Workspace accounts.',
      },
      {
        type: 'highlight',
        title: 'What made this incident different',
        items: [
          'The entry point was a small third-party AI SaaS tool - not a direct breach of the deployment platform',
          'OAuth refresh tokens persisted silently, turning a personal productivity signup into an enterprise lateral-movement path',
          'Production credentials lived in a multi-tenant control plane UI, not a dedicated secrets store with least-privilege access',
          'Classification gaps meant secrets stored as ordinary env vars were readable once an attacker had internal access',
        ],
      },
      {
        type: 'heading',
        text: 'The blast radius of platform-hosted secrets',
      },
      {
        type: 'paragraph',
        text: "Deployment platforms make it easy to colocate secrets with application configuration. That convenience hides a structural risk: your production credentials inherit the security model, access paths, and incident history of someone else's platform. When an attacker reaches the env-var management layer - through phishing, OAuth abuse, insider access, or a vendor compromise - they are one enumeration step away from database URLs, signing keys, and third-party API tokens.",
      },
      {
        type: 'paragraph',
        text: 'The Vercel breach is not an argument against using managed hosting. It is an argument against treating that hosting layer as your secrets vault. Secrets need their own lifecycle: encryption, rotation, audit logging, dynamic credentials, and access policies tied to your identity provider and network boundaries - not a checkbox on a deployment dashboard.',
      },
      {
        type: 'heading',
        text: 'What teams should do now',
      },
      {
        type: 'paragraph',
        text: 'If you run workloads on Vercel or similar platforms, start with inventory and rotation: treat every non-sensitive environment variable that could grant production access as potentially exposed. Enable sensitive classification where the platform supports it, enforce team policies that default new secrets to protected storage, and review OAuth grants across Google Workspace and other identity providers for AI and productivity tools with excessive scopes.',
      },
      {
        type: 'paragraph',
        text: 'Longer term, move production secrets to infrastructure you govern. HashiCorp Vault, OpenBao, and Infisical are built for this job: centralized secret storage, fine-grained policies, rotation workflows, and integration with Kubernetes, CI/CD, and application runtimes - running in your VPC, private cloud, or on-premise environment rather than a shared multi-tenant control plane.',
      },
      {
        type: 'heading',
        text: 'Private secrets management as architecture',
      },
      {
        type: 'paragraph',
        text: 'Where you store secrets is a security decision, not a DevOps convenience. Platform env vars are fine for non-production toggles and public configuration. They are a poor primary home for credentials that unlock databases, payment systems, or customer data - especially when a single OAuth misconfiguration at a vendor can bridge from corporate identity to deployment settings.',
      },
      {
        type: 'paragraph',
        text: 'Ametnes helps teams deploy and operate private secrets infrastructure alongside the rest of their open-source stack: Vault, OpenBao, or Infisical in environments you control, with the day-2 operations - TLS, patching, upgrades, backups, and enterprise support - that make secrets management production-ready rather than a one-off install.',
      },
    ],
  },
  {
    slug: 'ametnes-partners-with-suse',
    title: 'Ametnes partners with SUSE',
    category: 'Partnership',
    publishedAt: '2026-05-17',
    displayDate: 'May 17, 2026',
    partnerName: 'SUSE',
    logoSrc: null,
    excerpt:
      'Ametnes and SUSE align around Rancher so enterprises can run Ametnes-managed open-source solutions on a managed Kubernetes platform in private environments-with full day-2 operations and enterprise support.',
    seoDescription:
      'Ametnes partners with SUSE to deliver managed open-source workloads on Rancher-backed Kubernetes in private infrastructure, with enterprise day-2 operations for stateful services.',
    blocks: [
      {
        type: 'lead',
        text: 'Ametnes has formally partnered with SUSE with a clear focus on Rancher-the Kubernetes management platform enterprises already use to run clusters at scale in private and hybrid environments. Together, the goal is a single path to managed open-source software on Rancher-backed infrastructure, not a patchwork of DIY cluster installs and hand-operated workloads.',
      },
      {
        type: 'heading',
        text: 'Rancher as the foundation, Ametnes on top',
      },
      {
        type: 'paragraph',
        text: 'For the Ametnes platform, Rancher is the relevant SUSE layer: it is how many teams already provision, secure, and operate Kubernetes across data centers and private clouds. This partnership lets customers procure Ametnes-managed open-source solutions running on a managed Rancher Kubernetes platform in their own environments-one operational story from cluster governance through the applications your teams depend on.',
      },
      {
        type: 'paragraph',
        text: 'Platform teams no longer need to treat “who runs Rancher and the clusters” as separate from “who runs Postgres, GitLab, Dremio, or the AI gateway.” Ametnes operationalizes the workload layer on Rancher with the same rigor applied to stateful services that are hard to run in production: persistent storage, controlled upgrades, backup semantics, and consistent access across namespaces and clusters.',
      },
      {
        type: 'highlight',
        title: 'What customers get',
        items: [
          'Managed Rancher Kubernetes in private environments, operated to enterprise standards',
          'Ametnes-managed open-source solutions deployed and run on that Rancher platform',
          'Automated TLS certificate management across services and ingress',
          'Security patching and vulnerability response as part of normal operations',
          'Managed upgrades with tested rollout patterns-not ad-hoc weekend projects',
          'Managed backups for stateful services with recovery aligned to your policies',
          'Enterprise support from Ametnes across the Rancher-backed stack you run privately',
        ],
      },
      {
        type: 'heading',
        text: 'Why Rancher and Ametnes together',
      },
      {
        type: 'paragraph',
        text: 'Rancher gives enterprises a proven control plane for multi-cluster Kubernetes-policy, access, upgrades, and visibility in regulated and security-conscious settings. Ametnes adds the automation-first layer that turns open-source selection into repeatable private deployment: catalog-driven installs, governance, and day-2 management for the solutions you standardize on, all running on the Rancher foundation you already trust.',
      },
      {
        type: 'paragraph',
        text: 'If you are standardizing on Rancher for private or hybrid Kubernetes and want AI-ready open source, data platforms, or core engineering tools without rebuilding operations for every new workload, this partnership is designed to shorten the path from procurement to production.',
      },
    ],
    cta: {
      label: 'Talk to us about on-prem deployments',
      href: 'https://cloud.ametnes.com/contact/',
    },
  },
  {
    slug: 'open-weight-models-commoditize-reasoning',
    title: 'Open-weight models are commoditizing reasoning - and that changes everything',
    category: 'AI Infrastructure',
    breadcrumbLabel: 'AI Infrastructure',
    publishedAt: '2026-07-21',
    displayDate: 'July 21, 2026',
    cardFooterLabel: 'Read the post',
    excerpt:
      'Chinese open-weight models - DeepSeek, Qwen, Kimi K3 - now match or beat the most expensive US frontier models on reasoning benchmarks. The premium for thinking is collapsing. For private AI infrastructure, this is the most important trend of 2026.',
    seoDescription:
      'Open-weight models are commoditizing AI reasoning: DeepSeek, Qwen, and Kimi K3 rival GPT-5 and Claude Fable 5 at a fraction of the cost. What this means for private AI infrastructure, procurement, and the enterprise model landscape.',
    blocks: [
      {
        type: 'lead',
        text: 'If you are paying per-token for a frontier reasoning model in July 2026, you are almost certainly overpaying. The gap between open-weight and closed models on reasoning has narrowed so much that the best model for a task - coding, knowledge work, agentic reasoning - might be open-weight, MIT-licensed, and something you can run on your own hardware. The premium that justified $600/month Claude Opus or pay-per-call GPT-5 has been evaporating release by release.',
      },
      {
        type: 'heading',
        text: 'The Chinese model pipeline is changing the landscape',
      },
      {
        type: 'paragraph',
        text: 'The pace from Chinese AI labs over the past 12 months has been relentless. DeepSeek-V3 showed that Mixture-of-Experts could deliver frontier capability at a fraction of the compute budget. Qwen 3 from Alibaba matched GPT-4-class models across coding and multilingual tasks. Then came Moonshot AI\'s Kimi K2 in July 2025 (1 trillion total parameters, 32B active), K2 Thinking in November (outperforming GPT-5 on reasoning), and now Kimi K3 on 16 July - the largest open-weight model ever published at 2.8 trillion parameters, competitive with Claude Fable 5 and GPT-5.6 Sol.',
      },
      {
        type: 'paragraph',
        text: 'K3 releases under a modified MIT license, following K2\'s path. A model that scores well on Humanity\'s Last Exam and SWE-Bench, and runs on commodity hardware with MoE activation, is not a research curiosity. It is a production option procurement teams can budget for in capex instead of opex.',
      },
      {
        type: 'heading',
        text: 'Reasoning as a commodity',
      },
      {
        type: 'paragraph',
        text: 'The term reasoning model once commanded a premium. OpenAI o1 launched at multiples of GPT-4 pricing. Claude Opus carried a $600/month price tag. DeepSeek-R1 changed the conversation by showing that reasoning capabilities could be achieved through reinforcement learning at a fraction of the training cost. K2 Thinking and now K3 have driven the point home - the technique is replicable, and the moat is gone.',
      },
      {
        type: 'paragraph',
        text: 'This is a textbook commoditization cycle. The differentiating capability - multi-step reasoning, chain-of-thought, self-correction - becomes table stakes within 12 to 18 months of the leader\'s release. The premium shifts from what the model can do to where and how you can run it.',
      },
      {
        type: 'highlight',
        title: 'The commoditization timeline',
        items: [
          '2023 - GPT-4 is the only reliable reasoning model. Pricing: $0.03/1K input tokens. No serious open-source alternative.',
          '2024 - Llama 3 405B and Mistral Large close the gap on standard benchmarks. DeepSeek-V2 shows MoE can compete at 1/10th the cost.',
          'H1 2025 - DeepSeek-R1 and Qwen 2.5 match o1 on math and coding. The reasoning premium starts compressing. OpenAI responds with GPT-4.1 price cuts.',
          'H2 2025 - K2 Thinking, Llama 4, and Mistral Large 2 beat GPT-5 on specific benchmarks. Moonshot publishes training methodology. The moat is gone.',
          'July 2026 - K3 competes with frontier models. Open-weight models cover every tier from lightweight coding assistants to frontier reasoning. The question is no longer capability - it is operational fit.',
        ],
      },
      {
        type: 'heading',
        text: 'The take away',
      },
      {
        type: 'paragraph',
        text: 'The take away is straightforward: as model capability becomes a commodity, the differentiator shifts to how you deploy, govern, and operate AI in your own environment. Private AI infrastructure - running models behind your network boundary, with your access controls, audit logs, and governance policies - is where the value moves. Model selection becomes a configuration choice rather than a vendor lock-in, and the teams that have already invested in private deployment infrastructure will be the ones that move fastest.',
      },
      {
        type: 'heading',
        text: 'What this means for enterprise AI procurement',
      },
      {
        type: 'paragraph',
        text: 'When the best model for knowledge-work reasoning is open-weight and MIT-licensed, the buying conversation shifts from which vendor\'s API to connect to, to which model to deploy, on what infrastructure, governed by which policies. The advantage of closed API models - simpler integration, no infrastructure responsibility - is offset by predictable and generally lower total cost of ownership when running open-weight models at sufficient scale.',
      },
      {
        type: 'paragraph',
        text: 'There is a political layer too. The US Treasury threatened sanctions on Chinese AI labs over distillation concerns on 21 July - the same week as K3\'s release. DeepSeek, Moonshot AI, and MiniMax were all accused by Anthropic in February of using fraudulent accounts to extract Claude capabilities. Any organization serving users in multiple jurisdictions needs to weigh model provenance, export controls, and supply-chain continuity alongside capability and price.',
      },
      {
        type: 'heading',
        text: 'The operational question is the strategic question',
      },
      {
        type: 'paragraph',
        text: 'When every team has access to frontier-level reasoning at commodity pricing, the differentiator is not the model - it is the infrastructure. Can you deploy K3 or Qwen 3 behind your own network boundary? Can you govern access, audit usage, control data flow, and swap models without changing application code? Can you maintain the same security posture for an open-weight model that you would for a closed API?',
      },
      {
        type: 'paragraph',
        text: 'That is where private AI infrastructure comes in. Running open-weight models in your VPC, on-prem, or in an air-gapped environment means you get the capability without the vendor dependency. Your inference gateway handles routing, guardrails, and observability. Your governance layer enforces policy regardless of which model is serving a given request. Model selection becomes a configuration change, not a procurement cycle.',
      },
      {
        type: 'paragraph',
        text: 'The race between US and Chinese labs will keep going - new benchmarks, new techniques, new pricing. But the window for treating reasoning capability as a competitive differentiator is closing. The teams that will win are the ones that treat model selection as infrastructure, not vendor management.',
      },
    ],
    cta: {
      label: 'Talk to us about private AI infrastructure',
      href: 'https://cloud.ametnes.com/contact/',
    },
  },
  {
    slug: 'ametnes-partners-with-dremio',
    title: 'Ametnes partners with Dremio',
    category: 'Partnership',
    publishedAt: '2025-11-15',
    displayDate: 'November 15, 2025',
    partnerName: 'Dremio',
    logoSrc: '/media/icons/Dremio.png',
    excerpt:
      'Ametnes is a Dremio Authorized Partner, helping teams run a privately hosted open data lakehouse with governed access to sensitive data and predictable cost-without pushing production datasets into public cloud SaaS.',
    seoDescription:
      'Ametnes partners with Dremio as an Authorized Partner to deliver managed private data lakehouse deployments for healthcare, telecommunications, finance, and other regulated industries.',
    blocks: [
      {
        type: 'lead',
        text: 'On November 15, 2025, Ametnes joined the Dremio Partner Program as an Authorized Partner-formalizing a collaboration focused on one problem enterprise data teams keep running into: they need lakehouse capabilities, but their data cannot leave the environments where security, privacy, and cost control are non-negotiable.',
      },
      {
        type: 'heading',
        text: 'Why a privately hosted data lakehouse',
      },
      {
        type: 'paragraph',
        text: 'Dremio delivers the open data lakehouse experience teams want for SQL analytics, semantic layers, and AI-ready data access. Ametnes operationalizes it inside your private cloud or on-premises estate so analysts and engineers connect to production data where it already lives-without standing up a parallel copy in a multi-tenant SaaS analytics product.',
      },
      {
        type: 'paragraph',
        text: 'That matters when ingestion and egress to cloud SaaS platforms become a material line item, when data residency rules block external processing, or when security teams require consistent identity, audit, and network boundaries around every query path. Keeping the lakehouse private preserves those controls while still giving teams self-service velocity.',
      },
      {
        type: 'highlight',
        title: 'Where teams see the strongest fit',
        items: [
          'Healthcare - clinical and operational datasets that must stay inside approved environments while still supporting research and analytics workflows',
          'Telecommunications - network, subscriber, and service data with strict isolation and high operational expectations on the platform underneath',
          'Financial services - trading, risk, and customer data where governance, retention, and cost predictability outweigh convenience of public-cloud analytics tiers',
        ],
      },
      {
        type: 'heading',
        text: 'Cost, security, and operations together',
      },
      {
        type: 'paragraph',
        text: 'Moving large volumes of sensitive data into cloud SaaS for analytics often introduces recurring ingest charges, cross-region transfer costs, and duplicated storage-on top of the compliance review each new export path requires. A privately hosted lakehouse on Ametnes lets you colocate compute with data, apply consistent access policy, and scale capacity on infrastructure you already operate.',
      },
      {
        type: 'paragraph',
        text: 'Through the partnership, Ametnes deploys and runs Dremio alongside the rest of your open-source stack with the same day-2 discipline: TLS management, patching, upgrades, backups for stateful components, and enterprise support-so the lakehouse is a managed platform service, not a one-off integration project.',
      },
      {
        type: 'paragraph',
        text: 'Together, the combination gives data and platform leaders a practical procurement path: Dremio for lakehouse capabilities, Ametnes for private deployment and ongoing operations, and your teams for models, semantic definitions, and governance rules that stay under your control.',
      },
    ],
    cta: {
      label: 'Explore Dremio on Ametnes',
      href: '/solutions/service-dremio',
    },
  },
  {
    slug: 'metabase-breach-private-deployment',
    title: "The Metabase breach: why your analytics layer doesn't belong in a shared environment",
    category: 'Security',
    breadcrumbLabel: 'Security',
    publishedAt: '2026-08-15',
    displayDate: 'August 15, 2026',
    cardFooterLabel: 'Read the analysis',
    excerpt:
      'Metabase disclosed a 0-day attack on its cloud and patched a vulnerability affecting self-hosted instances from version 58 up. For Ametnes customers, the fix is a version bump - in the UI or one line in Terraform.',
    seoDescription:
      "The 6 August 2026 Metabase security incident explained: the reset_password 0-day, affected versions and minimum safe releases, and how Ametnes customers upgrade Metabase in a private, customer-controlled environment.",
    blocks: [
      {
        type: 'lead',
        text: "On 6 August 2026, Metabase disclosed a security incident affecting both its cloud and self-hosted users. An attacker exploited an unknown ('0-day') vulnerability in Metabase version 58 and above against Metabase Cloud. Metabase immediately blocked the endpoints used in the attack, identified and patched the vulnerability, and published a security advisory alongside minimum safe releases for every affected version. For Ametnes customers, this is a version-upgrade problem, and it is one you can fix in minutes without exposing your analytics layer to someone else's timeline.",
      },
      {
        type: 'heading',
        text: 'What happened',
      },
      {
        type: 'paragraph',
        text: "Metabase Cloud was attacked using a 0-day vulnerability in Metabase versions 58 and above. Metabase responded by blocking the endpoints used in the attack, then identifying and patching the vulnerability. Cloud customers were upgraded automatically. The open question - and the one that matters most here - is self-hosted instances.",
      },
      {
        type: 'paragraph',
        text: "A self-hosted Metabase on an affected version remains vulnerable. After gaining access to your instance, the attacker can inject arbitrary SQL against the Metabase application database, which can give them administrator access. From there they can change your application configuration, steal stored credentials for your connected databases, read any data accessible through those connections, and export it. In practice, the blast radius is every database your Metabase can reach.",
      },
      {
        type: 'heading',
        text: 'The vulnerability and how to spot an attack',
      },
      {
        type: 'paragraph',
        text: "The affected endpoint is `/api/session/reset_password`. Metabase's advisory flags the following pattern in application or ingress logs as a strong signal that an instance has been compromised:",
      },
      {
        type: 'code',
        label: 'Attack pattern in your logs',
        code: `POST /api/session/reset_password  -> 400
GET  /api/user/current            -> 200`,
      },
      {
        type: 'paragraph',
        text: "If you see that pair, treat the instance as potentially compromised and follow the remediation steps below.",
      },
      {
        type: 'heading',
        text: 'Affected versions and minimum safe releases',
      },
      {
        type: 'paragraph',
        text: "Metabase version 58 and above is affected; versions below 58 are not vulnerable. If you are running a point release below the minimum safe release for your version, you are still vulnerable and should upgrade. The minimum safe releases are:",
      },
      {
        type: 'highlight',
        title: 'Minimum safe releases per version',
        items: [
          '0.58.24 - Docker image: metabase/metabase:v0.58.24',
          '0.59.21 - Docker image: metabase/metabase:v0.59.21',
          '0.60.17 - Docker image: metabase/metabase:v0.60.17',
          '0.61.11 - Docker image: metabase/metabase:v0.61.11',
          '0.62.9 - Docker image: metabase/metabase:v0.62.9',
          '0.63.5 - Docker image: metabase/metabase:v0.63.5',
        ],
      },
      {
        type: 'heading',
        text: 'What self-hosters should do after upgrading',
      },
      {
        type: 'paragraph',
        text: "Metabase's advisory lists the immediate steps for anyone whose `/api/session/reset_password` endpoint was publicly reachable: revoke all active user sessions by deleting rows in the `core_session` table, review and remove unrecognized API keys, review administrator accounts for unexpected changes, rotate credentials for any connected databases, and review data-warehouse and query logs for unauthorized activity. If you cannot upgrade right away, block the `/api/session/reset_password` endpoint as a temporary workaround.",
      },
      {
        type: 'heading',
        text: 'Upgrading Metabase on Ametnes',
      },
      {
        type: 'paragraph',
        text: "The first line of defence is to run Metabase in a customer-controlled environment that is only accessible to you. On the Ametnes platform, Metabase is deployed inside your private environment and is only reachable within your own internal network - no shared tenancy, no public control plane, and no data leaving your perimeter.",
      },
      {
        type: 'paragraph',
        text: "With that foundation in place, upgrading should be a matter of selecting the newer version and clicking Update in the UI. The platform provisions Metabase as a private, customer-controlled resource, so the patch ships as a version change you approve on your own schedule - not a forced maintenance window on a shared platform.",
      },
      {
        type: 'paragraph',
        text: "If you manage Metabase with the Ametnes Terraform provider, you do not orchestrate the upgrade yourself. Simply update the `kind` with the newer version and the Ametnes agent performs all the necessary orchestration to upgrade - pulling the new image and rolling it out - inside your environment.",
      },
      {
        type: 'code',
        label: 'main.tf - bump the Metabase version',
        code: `resource "ametnes_service" "metabase" {
  name     = "analytics"
  project  = data.ametnes_project.default.id
  location = data.ametnes_location.primary.id

  # Update this line to the latest safe point release, then apply.
  kind = "metabase:0.63.5"

  spec = {
    visibility = "public"
    config = {
      architecture = "starter"
    }
  }
}`,
      },
      {
        type: 'paragraph',
        text: "Everything stays inside your private controlled environment - your data, your credentials, and the upgrade path itself.",
      },
      {
        type: 'paragraph',
        text: "If you have not yet provisioned Metabase privately, follow the step-by-step [provisioning tutorial](https://cloud.ametnes.com/docs/tutorials/metabase/) to deploy it in your own environment and connect it to your data.",
      },
      {
        type: 'heading',
        text: 'The lesson: control the layer, not just the tool',
      },
      {
        type: 'paragraph',
        text: "Metabase is a capable tool, and this incident is not a reason to abandon it. It is a reason to run it somewhere you can patch it on your own terms. In a private, customer-controlled environment you set the security posture, you audit access, and when a vulnerability like this lands, you upgrade without waiting on a vendor's incident response or exposing your instance to a shared control plane.",
      },
      {
        type: 'paragraph',
        text: "Your dashboards, your data, your perimeter - and now your upgrade, too.",
      },
    ],
    cta: {
      label: 'Talk to us about private Metabase',
      href: 'https://cloud.ametnes.com/contact/',
    },
  },
  {
    slug: 'deepseek-price-increase-public-ai',
    title: 'DeepSeek raises prices: the hidden cost of renting your AI',
    category: 'AI Infrastructure',
    breadcrumbLabel: 'AI Infrastructure',
    publishedAt: '2026-08-23',
    displayDate: 'August 23, 2026',
    cardFooterLabel: 'Read the analysis',
    excerpt:
      "DeepSeek - the low-cost leader that reset the market's expectations of what AI should cost - has raised its prices. It is clear, public AI pricing is a decision you do not get to make.",
    seoDescription:
      "Analysis of DeepSeek's price increase and why it matters for teams building on public AI APIs: one-sided pricing, hidden lock-in, and the case for running open-weight models on private infrastructure you control.",
    blocks: [
      {
        type: 'lead',
        text: 'DeepSeek built its reputation on being cheap - so cheap that it forced the entire frontier market to reprice. So when DeepSeek announces a price increase, the instinct is to shrug it off as a correction. This is a reminder that when you rent your AI from a public provider, pricing is a decision you never get to make. The number on the invoice can change under you, and there is nothing in your contract that protects you from it.',
      },
      {
        type: 'heading',
        text: 'The company that reset the market',
      },
      {
        type: 'paragraph',
        text: 'DeepSeek changed the conversation in late 2024 and early 2025. V3 delivered frontier-class capability at a fraction of the compute budget, and R1 showed that reasoning could be built with reinforcement learning at a fraction of the cost of the closed labs. Almost overnight, the assumption that a reasoning model had to carry a premium collapsed. OpenAI and Anthropic responded with cuts of their own, and teams began budgeting for AI as if cheap was the new permanent baseline.',
      },
      {
        type: 'paragraph',
        text: "That baseline was never guaranteed. DeepSeek's pricing was an aggressive, capital-subsidised land grab - not a promise. Now it has begun to unwind, and the reaction tells you more than the price table does.",
      },
      {
        type: 'heading',
        text: 'The repricing no customer voted on',
      },
      {
        type: 'paragraph',
        text: 'DeepSeek has announced an increase to its API pricing. The specifics matter less than the mechanics: the decision was made unilaterally, it applies to everyone running against the same endpoint, and it lands on the same invoice that was sold as the cheap option. There is no negotiation, no grandfathering for your workloads, and no guarantee it will not happen again.',
      },
      {
        type: 'highlight',
        title: 'What you inherit when you build on a public provider',
        items: [
          'Pricing is a one-sided decision - you read about it when it is already done',
          'Terms of service can shift underneath you without your sign-off',
          'Workloads and data woven into a third-party API are costly to move',
          "Your unit economics are hostage to someone else's roadmap",
        ],
      },
      {
        type: 'heading',
        text: 'The cost compounds quietly',
      },
      {
        type: 'paragraph',
        text: 'Teams that integrated DeepSeek - or any public model - directly into their products now face a re-architecture decision; absorb the increase, renegotiate nothing, or rip out and re-plumb the integration. Each option carries cost, and none of them was on the roadmap when the cheap integration was chosen.',
      },
      {
        type: 'paragraph',
        text: "This is the pattern, not the exception. A price change you did not sign off on, terms that shift under you, and workloads you cannot move because they are woven into a third-party API. What looked like a simple integration decision becomes a standing dependency on someone else's pricing roadmap.",
      },
      {
        type: 'heading',
        text: 'Run open-weight models where you control the costs',
      },
      {
        type: 'paragraph',
        text: "The antidote is not to avoid DeepSeek - or any single vendor - it is to stop renting the model by the token in a way that hands someone else the pricing lever. Open-weight models can be served on infrastructure you control, behind your network boundary, with your own access controls and audit logs. Once the model runs in your environment, a vendor's price change is no longer a line item on your invoice - it is someone else's problem.",
      },
      {
        type: 'paragraph',
        text: "Private deployment changes the equation in three ways; predictable, capital-based cost instead of a per-token meter that can be repriced at will; no dependency on a third party's pricing roadmap; and full control over where your data and workloads live. The models - including DeepSeek's own open weights - are the commodity. The control is the differentiator.",
      },
      {
        type: 'paragraph',
        text: 'Ametnes helps teams deploy and operate private AI infrastructure - including open-weight models and the gateways in front of them - in VPC, on-premise, and hybrid estates. That means capacity planning, ingress and TLS, observability on spend and latency, and day-2 operations so the serving layer stays as reliable as the models behind it.',
      },
    ],
    cta: {
      label: 'Talk to us about private AI infrastructure',
      href: 'https://cloud.ametnes.com/contact/',
    },
  },
];

export const getPostBySlug = (slug) => blogPosts.find((post) => post.slug === slug);

export const getSortedPosts = () =>
  [...blogPosts].sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
